import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import type { InitialState } from '@astrologermatch/shared';
import { Layout } from './components/Layout.js';
import { Spinner } from './components/common.js';
import { PageProvider, usePage } from './lib/state.js';
import { SessionProvider } from './lib/session.js';
import { Home } from './pages/Home.js';
import { PublicProfile, ProfileInactive, ProfileUnavailable } from './pages/PublicProfile.js';
import { ClaimProfile } from './pages/ClaimProfile.js';
import { RemoveProfile } from './pages/RemoveProfile.js';
import { ReviewPage } from './pages/ReviewPage.js';
import { VerifyPage } from './pages/VerifyPage.js';
import { LegalPage } from './pages/Legal.js';
import { AstrologerAuth } from './pages/AstrologerAuth.js';
import { NotFound } from './pages/NotFound.js';

// Client-rendered areas (noindex): loaded on demand so public pages stay small.
const AppRoutes = lazy(() => import('./pages/AppRoutes.js'));

function AppLoading() {
  return (
    <div className="wrap page-pad">
      <Spinner />
    </div>
  );
}

function PageSwitch() {
  const page = usePage();
  switch (page.kind) {
    case 'home':
      return <Home />;
    case 'astrologerAuth':
      return <AstrologerAuth />;
    case 'profile':
      return <PublicProfile data={page} />;
    case 'claimProfile':
      return <ClaimProfile data={page} />;
    case 'removeProfile':
      return <RemoveProfile data={page} />;
    case 'review':
      return <ReviewPage data={page} />;
    case 'verifyCertificate':
      return <VerifyPage data={page} />;
    case 'legal':
      return <LegalPage page={page.page} />;
    case 'app':
      return (
        <Suspense fallback={<AppLoading />}>
          <AppRoutes />
        </Suspense>
      );
    case 'notFound':
      return (
        <Routes>
          <Route path="/astrologers/*" element={<ProfileUnavailable />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      );
    case 'profileInactive':
      return <ProfileInactive />;
  }
}

export function App({ initial }: { initial: InitialState }) {
  return (
    <SessionProvider>
      <PageProvider initial={initial}>
        <Layout>
          <PageSwitch />
        </Layout>
      </PageProvider>
    </SessionProvider>
  );
}
